// src/styles/displaySettingsModalStyles.js
import { styled, withStyle } from 'baseui';
import { DarkTheme } from 'baseui';
import { Button } from 'baseui/button';

export const ModalContentWrapper = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '3dvh',
}));

export const SettingsSectionContainer = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale300,
}));

export const SettingsRow = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale300,
}));

export const SettingsLabel = styled('label', ({ $theme }) => ({
  color: $theme.colors.contentPrimary,
  fontWeight: 'normal',
  minWidth: 'auto',
  flexShrink: 0,
}));

export const SettingsInputGroup = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale300,
}));

export const StretchedSettingsInputGroup = withStyle(SettingsInputGroup, {
  flexGrow: 1,
});

export const iconSizes = {
  HEADER: 18,
  ACTION: 16,
};

export const StretchedInputOverrides = {
  Root: { style: { flexGrow: 1 } },
};

export const MaxDurationInputOverrides = {
  Root: {
    style: {
      width: '70px',
    },
  },
};

export const HiddenFileInput = styled('input', {
  display: 'none',
});

export const createThemedModalOverrides = (palette, theme) => ({
  Dialog: {
    style: {
      backgroundColor: palette.backgroundSecondary,
      border: `1px solid ${palette.borderOpaque}`,
      color: palette.contentPrimary,
      display: 'flex',
      flexDirection: 'column',
      [`@media screen and (max-width: ${theme.breakpoints.large - 1}px)`]: {
        width: '100%',
        minHeight: '100dvh',
        maxWidth: '100vw',
        margin: 0,
        borderRadius: 0,
        top: 0,
        left: 0,
      },
    },
  },
  ModalHeader: {
    style: {
      backgroundColor: palette.backgroundSecondary,
      borderBottom: `1px solid ${palette.borderOpaque}`,
    },
  },
  ModalBody: {
    style: {
      flex: 1,
      backgroundColor: palette.backgroundPrimary,
      color: palette.contentPrimary,
    },
  },
  ModalFooter: {
    style: {
      backgroundColor: palette.backgroundSecondary,
      color: palette.contentPrimary,
      borderTop: `1px solid ${palette.borderOpaque}`,
    },
  },
  Close: {
    style: {
      color: palette.contentPrimary,
    },
  },
});

export const createThemedTabsOverrides = (palette) => ({
  Root: {
    style: ({ $orientation }) => {
      if ($orientation === 'vertical') {
        return {
          display: 'flex',
          flexDirection: 'row',
          height: '100%',
        };
      }
      return {};
    },
  },
  TabList: {
    style: ({ $orientation, $theme }) => {
      if ($orientation === 'vertical') {
        return {
          display: 'flex',
          flexDirection: 'column',
          width: '180px',
          flexShrink: 0,
          backgroundColor: palette.backgroundSecondary,
          borderRight: `1px solid ${palette.borderOpaque}`,
          paddingTop: $theme.sizing.scale600,
          paddingBottom: $theme.sizing.scale600,
        };
      }
      return {
        display: 'grid',
        gridAutoFlow: 'column',
        gridAutoColumns: '1fr',
        width: '100%',
        backgroundColor: palette.backgroundSecondary,
      };
    },
  },
  Tab: {
    style: ({ $active, $orientation, $theme }) => {
      const baseStyles = {
        color: palette.contentPrimary,
        backgroundColor: $active ? palette.backgroundSecondary : palette.backgroundPrimary,
        ':hover': { backgroundColor: palette.backgroundSecondary },
      };
      if ($orientation === 'vertical') {
        return {
          ...baseStyles,
          borderLeft: $active ? `3px solid ${palette.borderOpaque}` : 'none',
          justifyContent: 'flex-start',
          paddingTop: $theme.sizing.scale500,
          paddingBottom: $theme.sizing.scale500,
          paddingLeft: $theme.sizing.scale600,
          paddingRight: $theme.sizing.scale600,
        };
      }
      return {
        ...baseStyles,
        borderBottom: $active ? `3px solid ${palette.borderOpaque}` : 'none',
        paddingTop: $theme.sizing.scale500,
        paddingBottom: $theme.sizing.scale500,
      };
    },
  },
  TabContent: {
    style: ({ $orientation, $theme }) => {
      if ($orientation === 'vertical') {
        return {
          flexGrow: 1,
          backgroundColor: palette.backgroundPrimary,
          padding: $theme.sizing.scale800,
          overflowY: 'auto',
        };
      }
      return {
        padding: 0,
        backgroundColor: palette.backgroundPrimary,
      };
    },
  },
});

export const createDarkThemeForModal = (palette) => ({
  ...DarkTheme,
  colors: {
    ...DarkTheme.colors,
    backgroundPrimary: palette.backgroundSecondary,
    backgroundSecondary: palette.backgroundPrimary,
    contentPrimary: palette.contentPrimary,
    inputFill: palette.inputFill,
    inputBorder: palette.inputBorder,
    inputText: palette.contentPrimary,
    buttonPrimaryFill: palette.buttonPrimaryFill,
    buttonPrimaryText: palette.buttonPrimaryText,
    buttonPrimaryFillHover: palette.buttonPrimaryFillHover,
    buttonPrimaryTextHover: palette.buttonPrimaryTextHover,
    buttonPrimaryFillActive: palette.buttonPrimaryFillActive,
    buttonPrimaryTextActive: palette.buttonPrimaryTextActive,
    buttonSecondaryFill: palette.buttonSecondaryFill,
    buttonSecondaryText: palette.buttonSecondaryText,
    buttonSecondaryFillHover: palette.buttonSecondaryFillHover,
    buttonSecondaryTextHover: palette.buttonSecondaryTextHover,
    buttonSecondaryFillActive: palette.buttonSecondaryFillActive,
    buttonSecondaryTextActive: palette.buttonSecondaryTextActive,
    buttonTertiaryFill: 'transparent',
    buttonTertiaryText: palette.contentPrimary,
    buttonTertiaryHover: palette.backgroundTertiary,
    toggleFill: palette.contentPrimary,
    toggleFillChecked: palette.accent,
  },
});

export const createDarkThemeCheckboxOverrides = (palette) => ({
  Label: {
    style: {
      color: `${palette.contentPrimary} !important`,
    },
  },
  Checkmark: {
    style: ({ $checked }) => ({
      backgroundColor: $checked ? palette.borderOpaque : 'transparent',
      borderTopColor: palette.contentPrimary,
      borderRightColor: palette.contentPrimary,
      borderBottomColor: palette.contentPrimary,
      borderLeftColor: palette.contentPrimary,
    }),
  },
});

export const ModalTitleText = styled('div', ({ $theme, $palette }) => ({
  color: $palette.contentPrimary,
  fontSize: $theme.typography.HeadingSmall.fontSize,
  fontWeight: $theme.typography.HeadingSmall.fontWeight,
  lineHeight: $theme.typography.HeadingSmall.lineHeight,
  [`@media screen and (max-width: ${$theme.breakpoints.large - 1}px)`]: {
    display: 'none',
  },
}));

export const SettingsHeading = styled('h4', ({ $theme, $palette }) => ({
  color: $palette.contentPrimary,
  marginTop: '0px',
  marginBottom: $theme.sizing.scale300,
}));

export const ThemeSelectionButton = styled(Button, ({ $theme, $palette }) => ({
  width: '120px',
  marginBottom: $theme.sizing.scale100,
  backgroundColor: $theme.colors.buttonPrimaryFill,
  color: $theme.colors.buttonPrimaryText,
  ':hover': {
    backgroundColor: $theme.colors.buttonPrimaryFillHover,
    color: $theme.colors.buttonPrimaryTextHover,
  },
  ':active': {
    backgroundColor: $theme.colors.buttonPrimaryFillActive,
    color: $theme.colors.buttonPrimaryTextActive,
  },
}));

export const SpeechTextPlaceholderContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
});

export const ButtonWrapContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

export const MenuGrid = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: $theme.sizing.scale300,
  marginBottom: $theme.sizing.scale600,
  borderBottom: `1px solid ${$theme.colors.borderOpaque}`,
  paddingBottom: $theme.sizing.scale400,
}));

export const MenuButton = styled('button', ({ $theme, $active, $palette }) => ({
  padding: `${$theme.sizing.scale300} ${$theme.sizing.scale500}`,
  backgroundColor: $active ? $palette.backgroundSecondary : 'transparent',
  color: $palette.contentPrimary,
  border: $active ? `1px solid ${$palette.borderOpaque}` : '1px solid transparent',
  borderRadius: $theme.borders.radius300,
  cursor: 'pointer',
  fontSize: $theme.typography.LabelMedium.fontSize,
  fontWeight: $active ? 'bold' : 'normal',
  transition: 'all 0.2s',
  ':hover': {
    backgroundColor: $palette.backgroundSecondary,
    border: `1px solid ${$palette.borderOpaque}`,
  },
  ':focus': {
    outline: 'none',
  },
}));
